import { useCallback, useContext, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Navigate, useParams, useSearchParams } from 'react-router'
import { ContentCopy, Settings } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, IconButton, Typography, useTheme } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'

import Bookmark from '../../components/bookmark'
import BreadcrumbsComponent from '../../components/breadcrumbs'
import StatusChip from '../../components/status-chip'
import { AppContext } from '../../providers/app-provider'
import Api from '../../utils/api'
import { formatAddress } from '../../utils/helpers'
import useClipboard from '../../utils/hooks/useClipboard'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import useFolderTheme from '../../utils/hooks/useFolderTheme'
import useSharedStyles from '../../utils/shared.styles'

import AccountTabsComponent from './account-view/components/tabs'
import TabPanelAbout from './components/about-tab-panel'
import AccountDetails from './components/account-details'
import TabPanelAlerts from './components/alerts-tab-panel'
import TabPanelDevices from './components/devices-tab-panel'
import { DeviceTableRow, GroupByKey } from './components/devices-tab-panel/types'
import TabPanelFolders from './components/folder-tab-panel'
import QuickActionsMenu from './components/folder-tab-panel/quick-actions-menu'
import GaugeChart from './components/gauge'
import TabPanelOrders from './components/orders-tab-panel'
import TabPanelPeople from './components/people-tab-panel'
import SearchComponent from './components/search'
import TabsComponent from './components/tabs'
import AccountsContext from './context'
import { getFolderAlerts } from './helpers'
import useStyles from './index.styles'

const Accounts = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const auth = useAuth()

  const { tree, treeRoots: allRoots, host, tenantId, pageRefreshCounter } = useContext(AppContext)
  const { accountId, tenantId: paramsTenantId } = useParams()
  const { clickToCopy } = useClipboard()
  const { enqueueSnackbar } = useSnackbar()
  const { folderTheme } = useFolderTheme(accountId ? Number(accountId) : undefined)
  const showStoreLink = folderTheme?.sensorStoreLink === true || folderTheme?.sensorStoreLink === 'true'

  const [searchParams, setSearchParams] = useSearchParams()
  const deviceGroupBy = (searchParams.get('groupBy') as GroupByKey) ?? 'alertSeverity'
  const tab = searchParams.get('tab')
  const setDeviceGroupBy = (newValue: GroupByKey) => setSearchParams((prev) => ({ ...prev, tab, groupBy: newValue }))

  const [searchText, setSearchText] = useState<string>('')

  const [folderRoots, setFolderRoots] = useState<number[]>([])
  const [folderTree, setFolderTree] = useState<IFolderTree | undefined>(tree)
  const [folder, setFolder] = useState<IFolderType | undefined>(undefined)
  const [folderAttributes, setFolderAttributes] = useState<IFolderAttributesType | undefined>(undefined)
  const [folderAttributesLoading, setFolderAttributesLoading] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [isLoadingAlerts, setIsLoadingAlerts] = useState<boolean>(true)
  const [deviceView, setDeviceView] = useState<'default' | 'compact' | 'micro'>('default')
  const [deviceAction, setDeviceAction] = useState<'pause' | 'unpause' | 'rma' | 'move' | 'deactivate' | undefined>(undefined)
  const [deviceDisplayFirmware, setDeviceDisplayFirmware] = useState<string[]>([])
  const [deviceHideDeactivated, setDeviceHideDeactivated] = useState<boolean>(false)
  const [pausedDeviceCount, setPausedDeviceCount] = useState<number>(0)
  const [firmwareVersions, setFirmwareVersions] = useState<string[]>([])

  const [pauseModalStatus, setPauseModalStatus] = useState<{ open: boolean; devices: DeviceTableRow[] }>({ open: false, devices: [] })
  const [unpauseModalStatus, setUnpauseModalStatus] = useState<{ open: boolean; devices: DeviceTableRow[] }>({ open: false, devices: [] })
  const [returnModalStatus, setReturnModalStatus] = useState<{ open: boolean; devices: DeviceTableRow[] }>({ open: false, devices: [] })
  const [moveDeviceModalStatus, setMoveDeviceModalStatus] = useState<{ open: boolean; devices: DeviceTableRow[] }>({ open: false, devices: [] })
  const [deactivateModalStatus, setDeactivateModalStatus] = useState<{ open: boolean; devices: DeviceTableRow[] }>({ open: false, devices: [] })
  const [activateModalStatus, setActivateModalStatus] = useState<{ open: boolean; device: DeviceTableRow | undefined }>({ open: false, device: undefined })

  const [alerts, setAlerts] = useState<IFolderAlertExtended[]>([])
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })
  const [isScrolled, setIsScrolled] = useState<boolean>(false)

  const [peopleTabRefreshCounter, setPeopleTabRefreshCounter] = useState<number>(0)
  const [folderRefreshCounter, setFolderRefreshCounter] = useState<number>(0)
  const [devicePhotoRefreshCounter, setDevicePhotoRefreshCounter] = useState<number>(0)

  const [denseViewForTableMultiselect, setDenseViewForTableMultiselect] = useState<boolean>(false)

  const [abortController, setAbortController] = useState<AbortController | undefined>(undefined)

  const folderChildCount = (tree && folder && tree[folder.id] && (tree[folder.id].children || []).length) ?? 0
  const isLoadingTree = !folderTree
  const isProgramView = folder?.type === 'Client Company'
  const isParticipantView = folder?.type === 'Program'
  const isAccountView = folder?.type === 'Participant'

  const showReactivateMenu = folder?.type === 'Participant' && folderAttributes?.accountStatus === 'Inactive'
  const showOptOutMenu = folder?.type === 'Participant' && folderAttributes?.accountStatus !== 'Inactive'

  const tabIndex = useMemo(() => {
    if (
      (tab && isProgramView && ['programs', 'people'].includes(tab)) ||
      (tab && isParticipantView && ['participants', 'people'].includes(tab)) ||
      (tab && isAccountView && ['accounts', 'devices', 'contacts', 'orders', 'alerts', 'about'].includes(tab)) ||
      (tab && !isProgramView && !isParticipantView && !isAccountView && ['accounts', 'people'].includes(tab))
    )
      return tab
    if (isProgramView) return 'programs'
    if (isParticipantView) return 'participants'
    if (isAccountView && folderChildCount === 0) return 'devices'
    if (isAccountView && folderChildCount > 0) return 'accounts'
    return 'accounts'
  }, [folderChildCount, isAccountView, isParticipantView, isProgramView, tab])

  useDocumentTitle(accountId && folder ? `${folder?.name} | Meshify Central` : 'Meshify Central')

  useEffect(() => {
    if (isLoadingTree) setFolderTree(tree)
  }, [isLoadingTree, tree])

  const fetchAlerts = useEffectEvent(async () => {
    if (!host || (accountId && !paramsTenantId) || !auth.user?.access_token) return
    if (folder && folder?.type === 'Participant' && accountId && Number(accountId) === folder.id) {
      const controller = new AbortController()
      const { signal } = controller
      setAbortController(controller)

      // Fetch the alerts
      try {
        const resAlerts = await getFolderAlerts(accountId, auth.user.access_token, host, signal)
        setAlerts(resAlerts)
      } catch (e) {
        // @ts-expect-error typescript doesn't know about the name property
        if (e?.name !== 'AbortError') {
          enqueueSnackbar('Something went wrong and we could not load the account alerts', { variant: 'error' })
        }
      }
    } else {
      setAlerts([])
      // also abort previous API calls to fetch alerts, if any
      abortController?.abort()
    }
    setIsLoadingAlerts(false)
  })

  useEffect(() => {
    if (folder) fetchAlerts()
  }, [folder, accountId, paramsTenantId, pageRefreshCounter])

  // Set folderTree whenever tree changes
  useEffect(() => {
    setFolderTree(tree)
  }, [tree])

  // Reset folder and folderRoots when accountId is removed from the url
  useEffect(() => {
    if (!accountId) {
      // If there is no account id, clear the folder and reset the folderRoots
      setFolder(undefined)
      setFolderAttributes(undefined)
      setFolderRoots(allRoots)
    }
  }, [accountId, allRoots])

  const fetchFolder = useEffectEvent(async () => {
    if (accountId && folderRefreshCounter > -1) {
      if (!host || !auth.user?.access_token) return
      // Fetch the folder when the accountId changes
      setIsLoading(true)
      const res = await Api.get(`${host}/api/folders/${accountId}`)
      if (res.ok) {
        setFolder(res.body)
        setFolderRoots([res.body.id])
      } else {
        enqueueSnackbar('Something went wrong and we could not load the account', { variant: 'error' })
        setIsLoading(false)
        return
      }

      if (res.body.type === 'Participant') {
        // Fetch the folder attributes - only participants have folder attributes
        setFolderAttributesLoading(true)
        const resAttributes = await Api.get(`${host}/api/v2/folders/${accountId}/attributes`)
        setFolderAttributesLoading(false)
        if (resAttributes.ok) {
          setFolderAttributes(resAttributes.body)
        } else {
          enqueueSnackbar('Something went wrong and we could not load the account attributes', { variant: 'error' })
        }
      }

      if (res.body.type === 'Participant' || res.body.type === 'Program') {
        // Fetch the participants and populate the folder tree data
        const resParticipants = await Api.get(`${host}/api/v2/protect/folders/${Number(accountId)}/participant-children`)
        if (resParticipants.ok) {
          const newTree = { ...tree }
          // Remove current folder from list then add each participant to the tree
          resParticipants?.body?.participants
            ?.filter((participant: IParticipantType) => participant.folder.id !== Number(accountId))
            ?.forEach((participant: IParticipantType) => {
              newTree[participant.folder.id] = { ...newTree[participant.folder.id], data: { ...participant, name: participant.folder.name } }
            })
          setFolderTree(newTree)
        } else {
          enqueueSnackbar("Something went wrong and we could not load all the information for the account's children", {
            variant: 'error',
          })
        }
      }
    }
    setIsLoading(false)
  })

  useEffect(() => {
    if (!isLoadingTree && ((accountId && tenantId) || !accountId)) fetchFolder()
  }, [isLoadingTree, accountId, tenantId, folderRefreshCounter, tree, host, pageRefreshCounter])

  const handleScroll = useCallback(() => {
    const bannerElement = document.getElementById('banner')
    const clientHeight = bannerElement?.clientHeight || 0
    if (window.scrollY > clientHeight) setIsScrolled(true)
    else setIsScrolled(false)
  }, [])

  useEffect(() => {
    // Adding the scroll listener
    window.addEventListener('scroll', handleScroll)

    return () => {
      // Removing listener
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const copyFolderInfo = () => {
    if (folder) {
      clickToCopy(`${folder?.name}\n${formatAddress(folder.information.address)}\nAcct #: ${folderAttributes?.clientAccountNumber ?? ''}`)
    }
  }

  if (accountId && tenantId && !paramsTenantId) {
    return <Navigate to={`/tenant/${tenantId}/accounts/${accountId}`} replace />
  }

  return (
    <AccountsContext.Provider
      value={{
        folder,
        folderAttributes,
        setFolderAttributes,
        folderAttributesLoading,
        folderTree,
        folderRoots,
        isParticipantView,
        isAccountView,
        isLoading,
        isLoadingAlerts,
        searchText,
        setSearchText,
        peopleTabRefreshCounter,
        setPeopleTabRefreshCounter,
        tabIndex,
        deviceView,
        setDeviceView,
        deviceAction,
        setDeviceAction,
        deviceGroupBy,
        setDeviceGroupBy,
        deviceDisplayFirmware,
        setDeviceDisplayFirmware,
        deviceHideDeactivated,
        setDeviceHideDeactivated,
        pausedDeviceCount,
        setPausedDeviceCount,
        firmwareVersions,
        setFirmwareVersions,
        pauseModalStatus,
        setPauseModalStatus,
        unpauseModalStatus,
        setUnpauseModalStatus,
        returnModalStatus,
        setReturnModalStatus,
        moveDeviceModalStatus,
        setMoveDeviceModalStatus,
        deactivateModalStatus,
        setDeactivateModalStatus,
        activateModalStatus,
        setActivateModalStatus,
        setFolderRefreshCounter,
        alerts,
        selectedDeviceIds,
        setSelectedDeviceIds,
        isScrolled,
        denseViewForTableMultiselect,
        setDenseViewForTableMultiselect,
        devicePhotoRefreshCounter,
        setDevicePhotoRefreshCounter,
      }}
    >
      <Box id="banner" sx={classes.banner}>
        <Box sx={classes.fullHeader}>
          <Box sx={classes.bannerImage} />
          <BreadcrumbsComponent folderId={Number(accountId)} />

          <Grid
            container={true}
            sx={{
              marginTop: 3,
            }}
          >
            {isAccountView && (
              <Grid sx={{ width: '260px' }}>
                <GaugeChart />
              </Grid>
            )}

            <Grid
              container={true}
              size={{
                sm: 'grow',
              }}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Grid sx={{ ...classes.folderNameWrapper(theme), minHeight: !isAccountView ? '160px' : '0' }}>
                {folderTree && folderRoots.length === 1 && (
                  <>
                    <Typography variant="h5" className="fs-exclude">
                      {isLoading && <CircularProgress size={16} />}
                      {!isLoading && folderTree[folderRoots[0]] && folderTree[folderRoots[0]].name}

                      {(folder?.type === 'Program' || isAccountView) && (
                        <IconButton onClick={() => copyFolderInfo()} aria-label="Copy account info to clipboard" size="small" sx={classes.copyBtn}>
                          <ContentCopy color="primary" sx={classes.copyIcon} />
                        </IconButton>
                      )}
                    </Typography>
                  </>
                )}

                {isAccountView && <AccountDetails folder={folder} folderAttributes={folderAttributes} folderTheme={folderTheme} />}
              </Grid>

              {(isAccountView || folder?.type === 'Program') && folder && (
                <Grid
                  sx={{
                    marginRight: 1,
                    marginLeft: -1,
                  }}
                >
                  <Grid container={true} sx={[sharedClasses.centerContentVertical, { gap: 1, marginBottom: 3 }]}>
                    <Bookmark type="account" id={folder?.id} displayName={folder?.name} />

                    <QuickActionsMenu
                      folderId={folder.id}
                      folderName={folder.name}
                      folderType={folder.type}
                      menuButtonElement={
                        <Button endIcon={<Settings />} aria-label="Toggle manage account menu">
                          Manage
                        </Button>
                      }
                      showStoreLink={showStoreLink}
                      showOptOutMenu={showOptOutMenu}
                      showReactivateMenu={showReactivateMenu}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>

        {isScrolled && (
          <Box sx={classes.minimizedHeader}>
            <Box sx={{ ...classes.bannerImage, ...classes.minimizedBannerImage }} />
            <div style={{ maxWidth: '1488px', margin: '0 auto' }}>
              <BreadcrumbsComponent folderId={Number(accountId)} minimized={true} />
              {folderTree && folderRoots.length === 1 && (
                <Grid container={true} wrap="nowrap" sx={[sharedClasses.centerContentVertical, { paddingTop: 1, width: 'calc(100% - 48px)' }]}>
                  <Typography
                    variant="h5"
                    className="fs-exclude"
                    sx={[
                      {
                        mt: 1,
                      },
                      classes.minimizedFolderName,
                    ]}
                  >
                    {folderTree[folderRoots[0]] && folderTree[folderRoots[0]].name}
                  </Typography>
                  {isAccountView && folderAttributes?.accountStatus && (
                    <Box sx={classes.accountViewChipContainer}>
                      {folderAttributes.accountStatus === 'Active' && <StatusChip label="Active" status="active" />}
                      {folderAttributes.accountStatus === 'Inactive' && <StatusChip label="Inactive" status="inactive" />}
                      {folderAttributes.accountStatus === 'Not Activated' && <StatusChip label="Not Activated" status="not activated" />}
                    </Box>
                  )}
                </Grid>
              )}
            </div>
          </Box>
        )}
      </Box>
      {/* 56px is the height of the table header that becomes sticky during scroll */}
      <Grid container={true} sx={{ flexDirection: 'column', ...classes.tabsContainer(theme), marginBottom: isScrolled ? '56px' : 0 }}>
        {isAccountView ? (
          <Grid
            container={true}
            direction="row"
            sx={[
              sharedClasses.centerContentVertical,
              {
                justifyContent: 'space-between',
                gap: 2,
              },
              classes.accountViewTabs,
            ]}
          >
            <AccountTabsComponent tabIndex={tabIndex} />
            {tabIndex !== 'about' && <SearchComponent />}
          </Grid>
        ) : (
          <Grid
            container={true}
            direction="row"
            sx={[
              sharedClasses.centerContentVertical,
              {
                justifyContent: 'space-between',
              },
              classes.treeViewTabs,
            ]}
          >
            <TabsComponent tabIndex={tabIndex} />
            <SearchComponent />
          </Grid>
        )}

        {(tabIndex === 'participants' || tabIndex === 'programs' || tabIndex === 'accounts') && <TabPanelFolders />}
        {tabIndex === 'devices' && <TabPanelDevices />}
        {(tabIndex === 'contacts' || tabIndex === 'people') && <TabPanelPeople />}
        {tabIndex === 'orders' && <TabPanelOrders />}
        {tabIndex === 'alerts' && <TabPanelAlerts />}
        {tabIndex === 'about' && <TabPanelAbout />}
      </Grid>
    </AccountsContext.Provider>
  )
}

export default Accounts
